import React, { useMemo, useState } from 'react';
import {
  HiOutlineCalendar,
  HiOutlineClipboardList,
  HiOutlineExclamationCircle,
  HiOutlineIdentification,
} from 'react-icons/hi';
import { supabase } from '../../utils/supabase';
import { generateRecordCode } from '../../utils/generateId';
import { isReservedFieldName } from '../../utils/config';
import { isCurrencyField } from '../../utils/currency';
import SuccessModal from '../feedback/SuccessModal';

function buildInitialValues(fields = []) {
  return fields.reduce((values, field) => {
    values[field] = '';
    return values;
  }, {});
}

function DynamicForm({ section, schoolId, termId, onRecordCreated }) {
  const editableFields = useMemo(
    () => (section?.fields || []).filter(field => !isReservedFieldName(field)),
    [section]
  );
  const ignoredFields = useMemo(
    () => (section?.fields || []).filter(field => isReservedFieldName(field)),
    [section]
  );

  const [values, setValues] = useState(() => buildInitialValues(editableFields));
  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(null);
  const [error, setError] = useState('');

  React.useEffect(() => {
    setValues(buildInitialValues(editableFields));
    setError('');
  }, [editableFields]);

  const handleChange = (field, value) => {
    setValues(current => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!schoolId || !section?.id || !termId) {
      setError('School, section, or term information is missing.');
      return;
    }

    if (!hasAtLeastOneValue(values, editableFields)) {
      setError('Enter at least one field before saving this record.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: existingRecords, error: fetchError } = await supabase
        .from('records')
        .select('record_code, record_id')
        .eq('section_id', section.id)
        .eq('term_id', termId);

      if (fetchError) throw fetchError;

      const recordCode = generateRecordCode(
        section.initials,
        (existingRecords || []).map(record => record.record_code || record.record_id)
      );
      const normalizedValues = normalizeFormValues(values, editableFields);

      const { data: createdRecord, error: insertError } = await supabase
        .from('records')
        .insert({
          school_id: schoolId,
          section_id: section.id,
          term_id: termId,
          record_code: recordCode,
          record_id: recordCode,
          data: normalizedValues,
        })
        .select('record_code, record_id')
        .single();

      if (insertError) throw insertError;

      setValues(buildInitialValues(editableFields));
      setSuccessModal({
        title: 'Record Saved',
        message: `${createdRecord?.record_code || createdRecord?.record_id || recordCode} was added to ${section.name}.`,
      });
      onRecordCreated?.();
    } catch (err) {
      console.error(err);
      setError(getSaveErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!section) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-5xl">
      <SuccessModal
        title={successModal?.title}
        message={successModal?.message}
        onClose={() => setSuccessModal(null)}
      />
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="border-b border-gray-100 px-5 py-5 dark:border-neutral-800 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-neutral-900 dark:text-blue-300">
                <HiOutlineClipboardList className="text-2xl" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Add New Record
                </p>
                <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-neutral-100">
                  {section.name}
                </h2>
              </div>
            </div>

            <div className="inline-flex w-fit items-center rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                {editableFields.length} fields
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 dark:border-neutral-800 dark:bg-neutral-950 md:grid-cols-2 sm:px-6">
          <ReadOnlyMeta
            icon={<HiOutlineIdentification />}
            label="Record ID"
            value={`${section.initials || 'REC'}-Auto`}
          />
          <ReadOnlyMeta
            icon={<HiOutlineCalendar />}
            label="Date & Time"
            value="Generated on save"
          />
        </div>

        <div className="px-5 py-6 sm:px-6">
          {editableFields.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 px-5 py-8 text-center text-sm text-blue-700 dark:border-blue-800 dark:bg-neutral-900 dark:text-blue-300">
              <p className="font-bold">No editable fields yet</p>
              <p className="mt-1">Add fields in Settings before creating records for this section.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {editableFields.map(field => (
                <label key={field} className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-neutral-200">
                    {field}
                  </span>
                  <div className="relative">
                    {isCurrencyField(field) && (
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">
                        {'\u20A6'}
                      </span>
                    )}
                    <input
                      type={isCurrencyField(field) ? 'number' : 'text'}
                      inputMode={isCurrencyField(field) ? 'decimal' : undefined}
                      min={isCurrencyField(field) ? '0' : undefined}
                      step={isCurrencyField(field) ? '0.01' : undefined}
                      value={values[field] || ''}
                      onChange={(event) => handleChange(field, event.target.value)}
                      className={`w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:ring-neutral-800 ${
                        isCurrencyField(field) ? 'pl-10' : ''
                      }`}
                      placeholder={`Enter ${field.toLowerCase()}`}
                    />
                  </div>
                </label>
              ))}
            </div>
          )}

          {ignoredFields.length > 0 && (
            <div className="mt-5 flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
              <HiOutlineExclamationCircle className="text-lg shrink-0 mt-0.5" />
              <p>
                System fields are generated automatically and were not added to this form:
                {' '}
                <span className="font-semibold">{ignoredFields.join(', ')}</span>.
              </p>
            </div>
          )}

          {error && (
            <div className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4 dark:border-neutral-800 dark:bg-neutral-950 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-sm text-gray-500 dark:text-neutral-400">
            Record ID and timestamp are locked by the system.
          </p>
          <button
            type="submit"
            disabled={loading || editableFields.length === 0}
            className="rounded-xl bg-blue-500 px-6 py-3 font-semibold text-white transition hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {loading ? 'Saving...' : 'Save Record'}
          </button>
        </div>
      </div>
    </form>
  );
}

function normalizeFormValues(values, fields) {
  return fields.reduce((nextValues, field) => {
    nextValues[field] = isCurrencyField(field)
      ? String(values[field] || '').replace(/[^\d.-]/g, '')
      : values[field];

    return nextValues;
  }, {});
}

function hasAtLeastOneValue(values, fields) {
  return fields.some(field => String(values[field] || '').trim() !== '');
}

function ReadOnlyMeta({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-neutral-800 dark:text-neutral-300">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {label}
        </p>
        <p className="truncate text-sm font-bold text-gray-700 dark:text-neutral-200">
          {value}
        </p>
      </div>
    </div>
  );
}

export default DynamicForm;

function getSaveErrorMessage(err) {
  if (!err?.message) {
    return 'Could not save this record. Please check your connection and Supabase table.';
  }

  return `Could not save this record: ${err.message}`;
}
